import {
  RiAddLine,
  RiAlarmWarningLine,
  RiApps2Line,
  RiArchiveDrawerLine,
  RiArchiveLine,
  RiArrowDownLine,
  RiArrowDownSLine,
  RiArrowGoBackLine,
  RiArrowLeftSLine,
  RiArrowRightLine,
  RiArrowRightSLine,
  RiArrowRightUpLine,
  RiArrowUpDownLine,
  RiArrowUpLine,
  RiBarChartBoxLine,
  RiBookOpenLine,
  RiBookShelfLine,
  RiBox3Line,
  RiBuilding2Line,
  RiCalendarLine,
  RiCheckboxCircleLine,
  RiCheckLine,
  RiCloseCircleLine,
  RiCloseLine,
  RiCodeBoxLine,
  RiCodeSSlashLine,
  RiCompass3Line,
  RiStopCircleLine,
  RiComputerLine,
  RiCornerDownLeftLine,
  RiCpuLine,
  RiCustomerService2Line,
  RiDashboard3Line,
  RiDatabase2Line,
  RiDeleteBinLine,
  RiDraftLine,
  RiDoorOpenLine,
  RiDownload2Line,
  RiEarthLine,
  RiEditLine,
  RiEditBoxLine,
  RiEqualizerLine,
  RiErrorWarningLine,
  RiExpandVerticalLine,
  RiExternalLinkLine,
  RiEyeLine,
  RiEyeOffLine,
  RiFile3Line,
  RiFileCopyLine,
  RiFileList3Line,
  RiFileTextLine,
  RiFlagLine,
  RiFlashlightLine,
  RiFlaskLine,
  RiFlowChart,
  RiFolder3Line,
  RiFolderSharedLine,
  RiGitBranchLine,
  RiGitCommitLine,
  RiGitMergeLine,
  RiGitPullRequestLine,
  RiGitRepositoryLine,
  RiGlobalLine,
  RiGroupLine,
  RiHardDrive2Line,
  RiHistoryLine,
  RiHomeLine,
  RiInformationLine,
  RiInstanceLine,
  RiKey2Line,
  RiLayoutGridLine,
  RiLeafLine,
  RiLineChartLine,
  RiListCheck3,
  RiListUnordered,
  RiLoader4Line,
  RiLockLine,
  RiLockPasswordLine,
  RiLoginBoxLine,
  RiMailLine,
  RiMailOpenLine,
  RiMailSendLine,
  RiMailSettingsLine,
  RiMap2Line,
  RiMapPinLine,
  RiMoneyEuroCircleLine,
  RiMoonLine,
  RiMore2Line,
  RiMoreLine,
  RiNodeTree,
  RiNotification3Line,
  RiOrganizationChart,
  RiPauseLine,
  RiPencilLine,
  RiPlayLine,
  RiPriceTag3Line,
  RiProhibitedLine,
  RiPulseLine,
  RiPuzzleLine,
  RiQuestionLine,
  RiRadarLine,
  RiRam2Line,
  RiRefreshLine,
  RiResetLeftLine,
  RiRocket2Line,
  RiRocketLine,
  RiRouteLine,
  RiSafe2Line,
  RiSave3Line,
  RiScales3Line,
  RiServerLine,
  RiSettings3Line,
  RiSettings4Line,
  RiShapesLine,
  RiShieldCheckLine,
  RiShieldFlashLine,
  RiShieldKeyholeLine,
  RiShieldUserLine,
  RiShip2Line,
  RiSignpostLine,
  RiStackLine,
  RiStackedView,
  RiStarFill,
  RiStarLine,
  RiSteering2Line,
  RiText,
  RiTimeLine,
  RiTranslate2,
  RiUploadCloud2Line,
  RiUserLine,
  RiUserSharedLine,
} from '@remixicon/react';
import type { RemixiconComponentType } from '@remixicon/react';

/**
 * The app's icon vocabulary: one entry per MEANING, not per glyph.
 *
 * Why Remix Icon: it is the set @backstage/ui itself draws with, so it is
 * already in the bundle and every icon of ours sits in the same line style as
 * the upstream ones next to it. The package ships one ESM module flagged
 * `sideEffects: false`, so only the icons named below are kept by the build.
 *
 * The rule: one meaning = one icon. A view that wants « a backup » takes
 * `waadooIcons.backup` and never picks a Remix glyph itself, so the same
 * notion cannot look different from one screen to the next, and changing the
 * glyph of a notion touches this file only. A new notion gets a new entry
 * here first.
 *
 * Remix icons draw on a 24x24 viewBox, `fill="currentColor"`, 24px by default
 * (`size` prop). They set no `aria-hidden`: a decorative use must pass it.
 */
export const waadooIcons = {
  project: RiFolder3Line,
  /**
   * An Odoo branch, permanent or ephemeral — an ephemeral one in its tier's
   * violet, with its ghost badge (`EnvironmentTag`, BranchLabel.tsx).
   * `devEnv` is the same notion, by another name.
   */
  branch: RiGitBranchLine,
  devEnv: RiGitBranchLine,
  /** The production branch (`PRODUCTION_BRANCH`, branchKind.ts). */
  production: RiShieldCheckLine,
  /** The environments of a project, as a whole (one per branch). */
  environments: RiInstanceLine,
  /**
   * L'adresse web d'un environnement (son site) : le globe que
   * `odooIcons.odooWebsite` dessine aussi, ajouté au cœur à l'étape 9d pour
   * les vues d'un environnement en sommeil (SleepingEnvironment.tsx,
   * EnvironmentPowerDialogs.tsx).
   */
  website: RiGlobalLine,
  pod: RiBox3Line,
  database: RiDatabase2Line,
  /** The Mailpit tool (caught mails); a mail itself is `mails`. */
  mailpit: RiMailLine,
  resources: RiDashboard3Line,
  cpu: RiCpuLine,
  memory: RiRam2Line,
  volume: RiHardDrive2Line,
  deployment: RiRocket2Line,
  /**
   * An environment's workloads being rolled out (the « Déploiement » step of
   * a branch being prepared): the cube of `pod`, which is what it starts.
   */
  rollout: RiBox3Line,
  revision: RiGitCommitLine,
  /** The container images an environment runs. */
  image: RiStackLine,
  /** A CI pipeline run of the forge (GitLab, GitHub). */
  ci: RiGitMergeLine,
  /**
   * A project without a pipeline of its own — a `standard` type, run from a
   * public image (« Sans CI », the home page's « Dernier pipeline »).
   */
  noCi: RiProhibitedLine,
  /** A merge request of the forge (the branch page's code line). */
  mergeRequest: RiGitPullRequestLine,
  /** Commits a branch has that its base lacks (« 3 en avance »). */
  commitsAhead: RiArrowUpLine,
  /** Commits the base has that a branch lacks (« 2 en retard sur dev »). */
  commitsBehind: RiArrowDownLine,
  backup: RiArchiveDrawerLine,
  backupNow: RiSave3Line,
  restore: RiArrowGoBackLine,
  history: RiHistoryLine,
  upload: RiUploadCloud2Line,
  logs: RiFileList3Line,
  mails: RiMailOpenLine,
  access: RiKey2Line,
  /** Signing in (the sign-in page's button; the `login` registry key). */
  login: RiLoginBoxLine,
  /** A wait with no status to report (the sign-in page's session check). */
  loading: RiLoader4Line,
  /**
   * Choosing a new password on Authelia's portal (the sign-in page's
   * « Mot de passe oublié ? »): the key of `access`, the same notion seen
   * from the account side.
   */
  passwordReset: RiKey2Line,
  /** Reaching the people who run the platform (« Contacter le support »). */
  support: RiCustomerService2Line,
  /**
   * Where the user comes back to once signed in (the sign-in page's
   * « Après connexion, vous reviendrez sur … »).
   */
  returnTo: RiCornerDownLeftLine,
  externalLink: RiExternalLinkLine,
  overview: RiPulseLine,
  refresh: RiRefreshLine,
  /** The home page (the branch page's house button). */
  home: RiHomeLine,
  /** Up to the page one came from (the branch page's « ‹ projet »). */
  back: RiArrowLeftSLine,
  /** Who created something (the project card's « Créé par »). */
  creator: RiUserLine,
  /** When something was created (the project card's creation date). */
  creationDate: RiCalendarLine,
  /** Editing a definition at its source (an entity's `backstage.io/edit-url`). */
  edit: RiEditLine,
  copy: RiFileCopyLine,
  show: RiEyeLine,
  hide: RiEyeOffLine,
  statusHealthy: RiCheckboxCircleLine,
  statusProgressing: RiLoader4Line,
  statusDegraded: RiErrorWarningLine,
  statusFailed: RiCloseCircleLine,
  /** A status nobody reported (Argo's `Unknown`, or no answer at all). */
  statusUnknown: RiQuestionLine,
  costs: RiMoneyEuroCircleLine,
  /** A file built from what is on screen, downloaded (« Exporter CSV »). */
  export: RiDownload2Line,
  /**
   * One step back, one step on, in an ordered list read one item at a time
   * (« Coûts plateforme »: « Mois précédent », « Mois suivant »).
   */
  previous: RiArrowLeftSLine,
  next: RiArrowRightSLine,
  /** What a button that opens a menu of choices ends with (the month's). */
  openMenu: RiArrowDownSLine,
  /** The metrics and dashboards tool (Grafana). */
  monitoring: RiBarChartBoxLine,
  /** What needs a look (the branch page's « À surveiller »). */
  attention: RiAlarmWarningLine,
  /** How long something should take (« Prêt dans 4 à 6 min »). */
  estimate: RiTimeLine,
  /** A node's machine type, its OVH flavour (« b3-8 »). */
  nodeFlavor: RiServerLine,
  /**
   * A node itself, one machine of the cluster (the tile of each row of the
   * « Nœuds » drawer, tinted by its load): the server of `nodeFlavor`, the
   * same machine seen whole.
   */
  node: RiServerLine,
  /**
   * One availability zone, a datacentre (the header of each zone card of the
   * « Nœuds » drawer); the zones as a whole are `zones`, the one zone of a
   * pod `podZone`.
   */
  availabilityZone: RiBuilding2Line,
  /**
   * Kubernetes itself, its version (the platform strip's cluster
   * line): the helm wheel of the project's logo.
   */
  kubernetes: RiSteering2Line,
  /** The container runtime of the nodes (containerd). */
  containerRuntime: RiShip2Line,
  /** The operating system of the nodes (« Ubuntu 22.04.5 LTS »). */
  operatingSystem: RiComputerLine,
  /** The cloud region of the cluster (« EU-WEST-PAR »); a zone is `zones`. */
  region: RiEarthLine,
  /** A node pool, OVH's group of nodes of one flavour. */
  nodePool: RiLayoutGridLine,
  /**
   * The room left to grow: a node pool's current nodes against its maximum
   * (the bar of its row), and the same summed over the autoscaled pools.
   */
  headroom: RiExpandVerticalLine,
  /** The software catalog. */
  catalog: RiBookShelfLine,
  /**
   * A scaffolder template the app has no glyph of its own for (a card of
   * « Créer », components/createPage).
   */
  template: RiDraftLine,

  // The tabs of « Créer » (components/createPage/subPages.ts).
  /** The templates one may launch, as a list (« Modèles »). */
  templateList: RiFileList3Line,
  /** The scaffolder's installed actions (« Actions »). */
  scaffolderActions: RiFlashlightLine,
  /** The scaffolder's template editor (« Éditeur de modèles »). */
  templateEditor: RiEditBoxLine,
  /** The scaffolder's templating extensions (« Extensions de modèles »). */
  templatingExtensions: RiPuzzleLine,
  /**
   * A link to another page of the app, out of the row it sits in (« Tâches »
   * at the end of the tabs of « Créer »); a page outside the app is
   * `externalLink`.
   */
  elsewhere: RiArrowRightUpLine,

  // The tabs of the settings page (`/settings`, modules/tabs).
  /** The user's general settings: profile, appearance (« Général »). */
  generalSettings: RiSettings4Line,
  /** The sign-in providers of the user (« Fournisseurs d'authentification »). */
  authProviders: RiShieldKeyholeLine,
  /** The feature flags (« Fonctionnalités expérimentales »). */
  featureFlags: RiFlagLine,

  // The tabs of the app visualizer (`/visualizer`, modules/tabs).
  /** The app's extensions as a tree (« Arbre »). */
  extensionTree: RiNodeTree,
  /** The same, as a detailed list (« Détaillé »). */
  extensionList: RiListUnordered,
  /** The same, as plain text (« Texte »). */
  extensionText: RiText,

  // The tabs of a catalog entity's page (the Odoo project header, modules/tabs).
  /** An entity's own page, its cards (« Aperçu »). */
  entityOverview: RiInformationLine,
  /** An entity's documentation, TechDocs (« Documentation »). */
  documentation: RiBookOpenLine,

  // The kinds of the catalog's entities (the catalog index's tabs and rows,
  // components/catalogIndex/kinds.ts); a Template is `template` above.
  /** A Component of the catalog (an Odoo project's entity among them). */
  catalogComponent: RiCodeBoxLine,
  /** A Resource of the catalog (a database, a bucket…). */
  catalogResource: RiDatabase2Line,
  /** A System of the catalog, the components and resources it gathers. */
  catalogSystem: RiServerLine,
  /** A Group of the catalog — an owner, as on a row's « Propriétaire ». */
  catalogGroup: RiGroupLine,
  /** A User of the catalog — an owner too, when a person owns something. */
  catalogUser: RiUserLine,
  /** A Location of the catalog, where its definitions are read from. */
  catalogLocation: RiMapPinLine,
  /** An API of the catalog. */
  catalogApi: RiCodeSSlashLine,
  /** A Domain of the catalog. */
  catalogDomain: RiCompass3Line,
  /**
   * An AppType of the catalog, the descriptor of an application type of the
   * platform (Odoo…) — the glyph of `odooApps` (odooIcons.ts), applications too.
   */
  catalogAppType: RiApps2Line,
  /** An entity of a kind the app has no glyph of its own for. */
  catalogEntity: RiFile3Line,
  /** An entity starred by the user (« Favoris »), and the toggle that stars it. */
  favorite: RiStarLine,
  /** The same, once starred. */
  favoriteOn: RiStarFill,
  /** Clearing the value of a filter (an active filter pill's « ✕ »). */
  clearFilter: RiCloseLine,

  // The « Composants » drawer of the home page's platform strip
  // (PlatformComponentList.tsx): the platform's own software, by group, then
  // by component.
  /** The platform's components, as a whole. */
  components: RiShapesLine,
  /** A version of a piece of software (the component's version badge). */
  softwareVersion: RiPriceTag3Line,
  /** The way into the cluster: ingress, certificates, DNS (the group). */
  clusterAccess: RiDoorOpenLine,
  /** The HTTP entry point, the ingress controller (Traefik). */
  ingress: RiRouteLine,
  /** TLS certificates (cert-manager and its OVH DNS-01 webhook). */
  certificate: RiShieldKeyholeLine,
  /** DNS records and resolution (external-dns, CoreDNS). */
  dns: RiSignpostLine,
  /** Who the users are and how they sign in (the group). */
  identity: RiShieldUserLine,
  /** The user directory (LLDAP); signing in itself is `login`. */
  userDirectory: RiOrganizationChart,
  /** Deployments driven from git (the group: Argo CD, Argo Workflows). */
  gitops: RiGitRepositoryLine,
  /**
   * Argo Workflows, the glyph of the `workflow` key of the icons registry
   * (modules/icons.tsx).
   */
  workflow: RiFlowChart,
  /** Metrics, logs and dashboards (the group). */
  observability: RiRadarLine,
  /** The metrics store (VictoriaMetrics); dashboards are `monitoring`. */
  metrics: RiLineChartLine,
  /** What keeps the data: databases, backups, secrets (the group). */
  dataServices: RiSafe2Line,
  /** Secrets synchronised from the vault (External Secrets). */
  secrets: RiLockPasswordLine,
  /** Volumes shared over the network (the NFS group); one volume is `volume`. */
  sharedStorage: RiFolderSharedLine,
  /** Sending mail (the group); a mail itself is `mails`. */
  mailService: RiMailSettingsLine,
  /** The SMTP relay the platform sends its mail through. */
  mailRelay: RiMailSendLine,
  /** The cluster's network (the group, and Cilium, its CNI). */
  network: RiNodeTree,

  // The odoo-project creation form (components/odooProjectForm/).
  /** The step summary of the form, before the review. */
  summary: RiListCheck3,
  /** The language of an Odoo instance. */
  language: RiTranslate2,
  add: RiAddLine,
  remove: RiCloseLine,
  /**
   * Deleting something for good (the home page's « Supprimer le projet… »);
   * not `remove`, which takes an item out of a list being edited.
   */
  delete: RiDeleteBinLine,
  /**
   * A run that changes nothing — the project deletion's simulation (the
   * « Tâches » page, components/tasks).
   */
  simulation: RiFlaskLine,
  /** A menu of further actions on one item (the « ⋮ » of a project row). */
  moreActions: RiMore2Line,
  /**
   * The same on a phone, where the header's other actions open a bottom
   * sheet (« ⋯ », ui/BottomSheet.tsx).
   */
  moreActionsSheet: RiMoreLine,
  /** Closing a panel or a sheet (ui/BottomSheet.tsx). */
  close: RiCloseLine,
  /** Stopping something that runs (the on-demand Odoo shell pod). */
  stop: RiStopCircleLine,
  /** Holding something that follows, to take it up again (the live logs). */
  pause: RiPauseLine,
  /** Taking up again what was paused (`pause`). */
  resume: RiPlayLine,
  /** What cannot be removed (the `dev` branch of a project). */
  locked: RiLockLine,
  /**
   * A permanent environment declared by the project's appenv repository but
   * not deployed — « En sommeil » (permanentEnvironments.ts). The moon of the
   * sidebar's dark-mode switch, which is a theme choice and not a notion of
   * this vocabulary.
   */
  sleeping: RiMoonLine,
  /**
   * Waking a sleeping environment — « Réveiller » (environmentPower.ts). The
   * rocket of the mockups envs-P1a and envs-B1; not `deployment`'s
   * (RiRocket2Line), which says a rollout already under way.
   */
  wake: RiRocketLine,
  /**
   * The same on a phone's environment line, where « Réveiller » is an icon
   * alone (mockup m3 « A1 », 2026-10-29: a bell).
   */
  wakeIconOnly: RiNotification3Line,
  /**
   * Promoting a permanent branch to the next one — the « Promotion » card
   * and its dialog (promotion.ts): the merge request it opens and merges,
   * as `mergeRequest`.
   */
  promotion: RiGitPullRequestLine,
  /** The step from one branch to the next in a promotion chain. */
  chainNext: RiArrowRightLine,
  /**
   * « Prochaine étape » of the « Promotion » card (mockup R3-A): the code
   * moving up the chain. The glyph of `elsewhere`, a notion of its own.
   */
  promotionNextStep: RiArrowRightUpLine,
  /**
   * A row that opens a page of its own — the « Tâches » page's rows, the
   * task's follow-up page.
   */
  openRow: RiArrowRightSLine,
  /**
   * A project the user may look at but not act on — a member of `prj-<p>`
   * without being an editor (`ReadOnlyBadge`). The eye of `show` and
   * `dbReplica`, the other two « reads only » notions; not `locked`, which
   * says that something cannot be removed at all.
   */
  readOnly: RiEyeLine,
  information: RiInformationLine,
  /**
   * A step of a task that is done, failed (the project deletion's task page,
   * components/taskProjectBanner/DeletionSteps.tsx): drawn in the step's
   * circle, where a status icon with its own circle would be one too many.
   */
  stepDone: RiCheckLine,
  stepFailed: RiCloseLine,
  /**
   * What the project deletion's report names (DeletionReport.tsx), besides
   * the GitLab, Argo CD and Grafana logos of the icons registry: an S3
   * bucket; a Crossplane resource (XR), the cube of `pod`; a group of the
   * user directory (LLDAP), not the directory itself (`userDirectory`); a
   * file of a repository (`catalog-info.yaml`, the environments' record).
   */
  bucket: RiArchiveLine,
  crossplaneResource: RiBox3Line,
  directoryGroup: RiGroupLine,
  repositoryFile: RiFileTextLine,
  /** What opens a collapsed block (turned when open, by CSS). */
  expand: RiArrowDownSLine,
  /** The capacity profiles of an environment. */
  profileLight: RiLeafLine,
  profileStandard: RiScales3Line,
  profileComfort: RiRocketLine,
  profileCustom: RiEqualizerLine,
  /**
   * The configuration of an environment — capacity, volumes, high
   * availability: the branch page's tab, its tasks.
   */
  configuration: RiSettings4Line,
  /** The Odoo processes of an environment (its workers). */
  processes: RiSettings3Line,
  /** The instances of a PostgreSQL cluster (primary and replicas). */
  replicas: RiStackedView,
  /** Back to a reference set of values (not a backup's `restore`). */
  reset: RiResetLeftLine,
  /** The high-availability options of an environment, as a whole. */
  highAvailability: RiShieldFlashLine,
  /**
   * Instances added and removed with the load: Odoo pods (HPA), and the
   * nodes of an OVH node pool (its autoscaler) — one notion.
   */
  autoscaling: RiArrowUpDownLine,
  /** Sessions shared between the Odoo pods (Valkey). */
  sessions: RiUserSharedLine,
  /** The cluster's availability zones, and placement across them. */
  zones: RiMap2Line,
  /** The one zone a pod runs in (the branch page's pods card). */
  podZone: RiMapPinLine,
  /**
   * A PostgreSQL instance that takes the writes, its primary; not `edit`,
   * which is editing a definition at its source.
   */
  dbPrimary: RiPencilLine,
  /** A PostgreSQL instance that only serves reads, a replica. */
  dbReplica: RiEyeLine,
} as const satisfies Record<string, RemixiconComponentType>;

export type WaadooIconName = keyof typeof waadooIcons;
